import { InvoiceItem, DocumentAllowance, CalculatedTotals } from '../types/invoiceTypes';

/**
 * Berechnet die Summen einer Liste von Rechnungs- oder Angebotspositionen.
 * Optional werden Dokumentebene-Rabatte/Aufschläge (BG-20/BG-21) berücksichtigt.
 * @param items Die Liste der Positionen
 * @param documentAllowances Optionale Dokumentrabatte/-aufschläge
 * @returns Die berechneten Summen (Netto, Steuer, Brutto) und eine Zusammenfassung nach Steuersätzen
 */
export const calculateTotals = (items: InvoiceItem[], documentAllowances?: DocumentAllowance[] | null): CalculatedTotals => {
  // Initialisierung der Summen
  let totalNet = 0;
  let totalTax = 0;
  const taxSummary: { [rate: string]: { base: number; amount: number } } = {};

  // Durchlaufen aller Positionen
  items.forEach(item => {
    // Berechnung des Nettobetrags für diese Position (basiert auf netPrice nach Positionsrabatt)
    const itemNetAmount = item.quantity * item.netPrice;
    totalNet += itemNetAmount;

    // Berechnung der Steuer für diese Position
    const taxRate = item.taxRate.toString();
    const taxAmount = itemNetAmount * (item.taxRate / 100);
    totalTax += taxAmount;

    // Aktualisierung der Steuersummierung nach Steuersatz
    if (!taxSummary[taxRate]) {
      taxSummary[taxRate] = { base: 0, amount: 0 };
    }
    taxSummary[taxRate].base += itemNetAmount;
    taxSummary[taxRate].amount += taxAmount;
  });

  // Dokumentebene Rabatte/Aufschläge anwenden (BG-20/BG-21)
  if (documentAllowances && documentAllowances.length > 0) {
    for (const allowance of documentAllowances) {
      const absAmount = allowance.type === 'percentage'
        ? totalNet * (allowance.value / 100)
        : allowance.value;
      const netAdjustment = allowance.isCharge ? absAmount : -absAmount;
      const taxAdjustment = netAdjustment * (allowance.taxRate / 100);
      const rateKey = allowance.taxRate.toString();

      totalNet += netAdjustment;
      totalTax += taxAdjustment;

      if (!taxSummary[rateKey]) {
        taxSummary[rateKey] = { base: 0, amount: 0 };
      }
      taxSummary[rateKey].base += netAdjustment;
      taxSummary[rateKey].amount += taxAdjustment;
    }
  }

  // Berechnung des Bruttobetrags
  const grandTotal = totalNet + totalTax;

  // Runden auf 2 Dezimalstellen für alle Geldbeträge
  totalNet = parseFloat(totalNet.toFixed(2));
  totalTax = parseFloat(totalTax.toFixed(2));
  const grandTotalRounded = parseFloat(grandTotal.toFixed(2));

  // Auch die Beträge in der Steuersummierung runden
  Object.keys(taxSummary).forEach(rate => {
    taxSummary[rate].base = parseFloat(taxSummary[rate].base.toFixed(2));
    taxSummary[rate].amount = parseFloat(taxSummary[rate].amount.toFixed(2));
  });

  return {
    totalNet,
    totalTax,
    grandTotal: grandTotalRounded, // Gerundeter Bruttobetrag
    taxSummary
  };
};